'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';

interface SignInFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

export default function SignInForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const onSubmit = async (data: SignInFormData) => {
    setAuthError('');
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back! Redirecting...');
      router.push('/');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Sign in failed. Please try again.';
      setAuthError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {/* Mobile logo */}
      <div className="flex items-center gap-3 mb-8 lg:hidden">
        <div className="p-1.5 rounded-xl" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <AppLogo size={36} />
        </div>
        <div>
          <span className="font-800 text-lg block" style={{ color: '#C9A84C', letterSpacing: '0.08em' }}>LIVORA OS</span>
          <span className="text-xs" style={{ color: 'rgba(201,168,76,0.55)' }}>Livora Groups</span>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-700 text-foreground">Sign in to Livora OS</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Access your organisation&apos;s command center
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Auth error */}
        {authError && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-danger/10 border border-danger/30">
            <Icon
              name="ExclamationCircleIcon"
              size={16}
              variant="outline"
              className="text-danger flex-shrink-0 mt-0.5"
            />
            <p className="text-sm text-danger">{authError}</p>
          </div>
        )}

        {/* Email */}
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-500 text-foreground mb-1.5"
          >
            Email address <span className="text-danger">*</span>
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@livora.in"
            {...register('email', {
              required: 'Email address is required.',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Enter a valid email address.',
              },
            })}
            className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
              errors.email
                ? 'border-danger focus:ring-danger/20' :'border-border focus:border-primary/50'
            }`}
          />
          {errors.email && (
            <p className="text-xs text-danger mt-1 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} variant="outline" />
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="text-sm font-500 text-foreground">
              Password <span className="text-danger">*</span>
            </label>
            <Link
              href="/forgot-password"
              className="text-xs font-500 transition-colors"
              style={{ color: '#C9A84C' }}
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              {...register('password', {
                required: 'Password is required.',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters.',
                },
              })}
              className={`w-full px-3.5 py-2.5 pr-10 bg-input border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors ${
                errors.password
                  ? 'border-danger focus:ring-danger/20' :'border-border focus:border-primary/50'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon
                name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'}
                size={16}
                variant="outline"
              />
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-danger mt-1 flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} variant="outline" />
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            id="rememberMe"
            type="checkbox"
            {...register('rememberMe')}
            className="w-4 h-4 rounded border-border bg-input accent-primary cursor-pointer"
          />
          <label
            htmlFor="rememberMe"
            className="text-sm text-muted-foreground cursor-pointer"
          >
            Keep me signed in for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 px-4 font-600 text-sm rounded-lg active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #C9A84C, #E8D5A3)', color: '#0A0F1E' }}
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" />
              <span>Sign In to Livora OS</span>
            </>
          )}
        </button>
      </form>

      <p className="text-xs text-muted-foreground/60 mt-6 text-center">
        Contact your administrator if you need access to Livora OS.
      </p>
    </div>
  );
}